import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../services/api';
import type { EmailTemplate } from '../../services/api';
import {
  Loader2, Save, AlertCircle, CheckCircle, X, ArrowLeft, Mail, Eye,
} from 'lucide-react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const SAMPLE_VARIABLES: Record<string, string> = {
  name: 'Alex',
  new_retention_days: 'Applied (30d)',
  status: 'trial',
  permissions_summary: 'Sample permissions text.',
  job_count: '3',
  source: 'LinkedIn',
};

function renderPreview(text: string, vars: Record<string, string>): string {
  if (!text) return '';
  return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, key) => {
    return vars[key] !== undefined ? vars[key] : `{{${key}}}`;
  });
}

export function Communications() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.listEmailTemplates();
      setTemplates(data.templates || []);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useAutoRefresh(loadTemplates);

  const selected = useMemo(
    () => templates.find(t => t.template_key === selectedKey) || null,
    [templates, selectedKey],
  );

  if (loading && templates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (selected) {
    return (
      <TemplateEditor
        template={selected}
        onClose={() => setSelectedKey(null)}
        onSaved={async (msg) => {
          setSuccess(msg);
          setTimeout(() => setSuccess(null), 3000);
          await loadTemplates();
        }}
      />
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-6 h-6" />
            Communications
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Edit transactional email templates. Add new templates in code via SEED_TEMPLATES, then run seed.
          </p>
        </div>
        <button
          onClick={async () => {
            try {
              const res = await api.seedEmailTemplates();
              setSuccess(`Seeded ${res.templates.length} template(s)`);
              setTimeout(() => setSuccess(null), 3000);
              await loadTemplates();
            } catch (err: unknown) {
              const axiosErr = err as { response?: { data?: { message?: string } } };
              setError(axiosErr?.response?.data?.message || 'Failed to seed templates');
            }
          }}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
        >
          Seed from Code
        </button>
      </div>

      {error && (
        <div className="flex items-center p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center p-3 mb-4 bg-green-50 text-green-700 rounded-lg text-sm">
          <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Template Key
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Subject
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Variables
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Updated
              </th>
            </tr>
          </thead>
          <tbody>
            {templates.map(t => (
              <tr
                key={t.template_key}
                onClick={() => setSelectedKey(t.template_key)}
                className="border-b border-gray-100 hover:bg-primary-50 cursor-pointer"
              >
                <td className="px-4 py-3 font-mono text-sm text-gray-900">{t.template_key}</td>
                <td className="px-4 py-3 text-sm text-gray-700 truncate max-w-md">{t.subject}</td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {(t.variables || []).map(v => (
                    <code key={v} className="inline-block bg-gray-100 px-1.5 py-0.5 rounded mr-1 mb-1">
                      {v}
                    </code>
                  ))}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {t.updated_at ? new Date(t.updated_at).toLocaleString() : '-'}
                </td>
              </tr>
            ))}
            {templates.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                  No templates yet. Click "Seed from Code" to load defaults.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


interface TemplateEditorProps {
  template: EmailTemplate;
  onClose: () => void;
  onSaved: (msg: string) => Promise<void> | void;
}

function TemplateEditor({ template, onClose, onSaved }: TemplateEditorProps) {
  const [subject, setSubject] = useState(template.subject || '');
  const [bodyHtml, setBodyHtml] = useState(template.body_html || '');
  const [bodyText, setBodyText] = useState(template.body_text || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);

  const previewVars = useMemo(() => {
    const vars: Record<string, string> = { ...SAMPLE_VARIABLES };
    for (const k of template.variables || []) {
      if (vars[k] === undefined) vars[k] = `[${k}]`;
    }
    return vars;
  }, [template.variables]);

  const previewSubject = useMemo(() => renderPreview(subject, previewVars), [subject, previewVars]);
  const previewHtml = useMemo(() => renderPreview(bodyHtml, previewVars), [bodyHtml, previewVars]);
  const previewText = useMemo(() => renderPreview(bodyText, previewVars), [bodyText, previewVars]);

  const dirty = (
    subject !== (template.subject || '') ||
    bodyHtml !== (template.body_html || '') ||
    bodyText !== (template.body_text || '')
  );

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await api.updateEmailTemplate(template.template_key, {
        subject,
        body_html: bodyHtml,
        body_text: bodyText,
      });
      await onSaved(`Template '${template.template_key}' saved`);
      onClose();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <button
        onClick={onClose}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4 text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to templates
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-mono">{template.template_key}</h1>
          <p className="text-sm text-gray-500 mt-1">
            Variables:{' '}
            {(template.variables || []).map(v => (
              <code key={v} className="inline-block bg-gray-100 px-1.5 py-0.5 rounded mr-1">
                {`{{${v}}}`}
              </code>
            ))}
            {(template.variables || []).length === 0 && <span className="text-gray-400">none</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPreview(p => !p)}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 font-medium"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className={`grid gap-6 ${showPreview ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-5">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
              Subject
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
            />
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
              Body HTML
            </label>
            <textarea
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono"
              rows={20}
            />
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
              Body Text (plain-text fallback)
            </label>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono"
              rows={10}
            />
          </div>
        </div>

        {showPreview && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                Preview Subject
              </h3>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                {previewSubject || <span className="text-gray-400">(empty)</span>}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                Preview HTML (rendered)
              </h3>
              <div
                className="border border-gray-200 rounded-lg overflow-auto"
                style={{ maxHeight: 600 }}
              >
                <iframe
                  title="preview-html"
                  srcDoc={previewHtml}
                  className="w-full"
                  style={{ minHeight: 600, border: 'none' }}
                />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                Preview Text
              </h3>
              <pre className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs whitespace-pre-wrap font-mono">
                {previewText || <span className="text-gray-400">(empty)</span>}
              </pre>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-700">
              <strong>Sample values used:</strong>{' '}
              {Object.entries(previewVars).map(([k, v]) => (
                <span key={k} className="inline-block mr-3">
                  <code className="bg-white px-1.5 py-0.5 rounded mr-1">{k}</code>
                  {v}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
